import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { setHeycatchUser, resetHeycatchUser } from "@/lib/heycatchAnalytics";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").trim().replace(/\/+$/, "");
export const API = `${BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionError, setSessionError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const refreshSequence = useRef(0);
  const membershipSequence = useRef(0);
  const verifiedActor = useRef(null);
  const [membershipsError, setMembershipsError] = useState("");
  const [membershipsLoading, setMembershipsLoading] = useState(false);
  // Phase 5C.1b — authoritative server-resolved workspace state.
  // `memberships` is the list the backend tells us this user is
  // allowed to inhabit. `activeWorkspace` is whatever the server
  // currently resolves per-request; the UI reads it, never writes.
  const [memberships, setMemberships] = useState([]);
  const [activeWorkspace, setActiveWorkspace] = useState(null);

  const refresh = useCallback(async () => {
    const sequence = ++refreshSequence.current;
    setRefreshing(true);
    try {
      const { data } = await axios.get(API + "/auth/me");
      if (sequence !== refreshSequence.current) return null;
      if (verifiedActor.current !== data?.id) {
        membershipSequence.current += 1;
        setMemberships([]); setMembershipsError(""); setMembershipsLoading(false);
      }
      verifiedActor.current = data?.id || null;
      setUser(data);
      setHeycatchUser(data);
      setActiveWorkspace(data?.active_workspace || data?.id || null);
      setSessionError("");
      return data;
    } catch (failure) {
      if (sequence !== refreshSequence.current) return null;
      const status = failure.response?.status;
      if (status === 401 || status === 403) {
        membershipSequence.current += 1; verifiedActor.current = null;
        setMembershipsLoading(false); setMembershipsError("");
        setUser(false); setActiveWorkspace(null); setMemberships([]);
        resetHeycatchUser();
        setSessionError("");
      } else {
        // Network/server failure is not evidence of logout. Preserve known
        // state; protected routes offer an explicit retry when identity is
        // still unknown. Server authorization remains authoritative.
        setSessionError("Session check is temporarily unavailable. Please try again.");
      }
      return null;
    } finally {
      if (sequence === refreshSequence.current) {
        setLoading(false); setRefreshing(false);
      }
    }
  }, []);

  const fetchMemberships = useCallback(async () => {
    const sequence = ++membershipSequence.current;
    const sessionSequence = refreshSequence.current;
    setMembershipsLoading(true);
    try {
      const { data } = await axios.get(API + "/auth/memberships");
      if (sequence !== membershipSequence.current || sessionSequence !== refreshSequence.current) return [];
      const result = data?.memberships || [];
      if (!Array.isArray(result)) throw new Error("Invalid workspace list response.");
      if (verifiedActor.current && data?.actor?.user_id !== verifiedActor.current) {
        throw new Error("Workspace list identity changed.");
      }
      setMemberships(result); setMembershipsError("");
      return result;
    } catch (failure) {
      if (sequence !== membershipSequence.current || sessionSequence !== refreshSequence.current) return [];
      if (failure.response?.status === 401 || failure.response?.status === 403) {
        setMemberships([]);
      }
      setMembershipsError("Workspace list is temporarily unavailable. Please try again.");
      return [];
    } finally {
      if (sequence === membershipSequence.current) setMembershipsLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // When we know who the user is, pull their memberships so the
  // switcher can render human-readable labels without hitting UUIDs.
  useEffect(() => {
    if (user?.business_demo && user?.is_trial) {
      // A fictional demo has no real-company workspace selector.
      membershipSequence.current += 1;
      setMemberships([]); setMembershipsLoading(false); setMembershipsError("");
      return;
    }
    if (user && user !== false) {
      fetchMemberships();
    } else {
      membershipSequence.current += 1; verifiedActor.current = null;
      setMemberships([]); setMembershipsLoading(false); setMembershipsError("");
    }
  }, [user, fetchMemberships]);

  const switchWorkspace = useCallback(async (workspace_owner) => {
    // Server validates membership. On success it:
    //  - updates active_sessions.active_workspace;
    //  - re-issues the zy_active_session cookie (http-only).
    // In the browser, reload immediately after success. Do not refresh
    // the new user's workspace context while old module state remains
    // mounted during extra /auth/me or membership requests.
    const { data } = await axios.post(
      `${API}/auth/switch-workspace`,
      { workspace_owner }
    );
    // Hard reload the dashboard so previous workspace module state is
    // discarded together, rather than mixing old views with a new context.
    if (typeof window !== "undefined") {
      window.location.assign("/dashboard");
    } else {
      // Preserve the non-browser fallback without browser navigation.
      await refresh();
      await fetchMemberships();
    }
    return data;
  }, [refresh, fetchMemberships]);

  const logout = useCallback(async () => {
    // A late refresh response must not restore identity after logout.
    refreshSequence.current += 1;
    membershipSequence.current += 1; verifiedActor.current = null;
    setMemberships([]); setMembershipsLoading(false); setMembershipsError("");
    setRefreshing(false); setSessionError(""); setLoading(false);
    // Stop replay and clear PostHog identity immediately on logout,
    // before waiting for the network request. This is privacy-sensitive
    // and must not depend on the backend's response time or availability.
    if (typeof window !== "undefined") {
      try {
        window.posthog?.stopSessionRecording?.();
        window.posthog?.reset?.();
      } catch { /* Analytics must never block logout. */ }
      try {
        resetHeycatchUser();
      } catch { /* Analytics must never block logout. */ }
    }

    try {
      await axios.post(`${API}/auth/logout`);
    } catch { /* Preserve existing local-logout behavior on API failure. */ }

    setUser(false);
    setMemberships([]);
    setActiveWorkspace(null);
  }, []);

  return (
    <AuthCtx.Provider
      value={{
        user,
        loading,
        sessionError,
        refreshing,
        setUser,
        refresh,
        logout,
        memberships,
        membershipsError,
        membershipsLoading,
        activeWorkspace,
        fetchMemberships,
        switchWorkspace,
      }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.message === "string") return detail.message;
  if (detail && typeof detail.msg === "string") return detail.msg;
  // Backend workspace errors come as {code, message} objects.
  if (detail && typeof detail === "object" && typeof detail.code === "string") {
    return detail.message || detail.code;
  }
  return String(detail);
}

